import { useState, useEffect, useMemo } from 'react';
import { useNavigation } from '@/contexts/NavigationContext';
import { cn } from '@/lib/utils';

// Lucide React Icons
import {
  Plus,
  Search,
  Eye,
  Edit,
  Trash2,
  X,
  AlertTriangle,
  CheckCircle,
  GitBranch,
  Users,
  Building2,
  Server,
  ShoppingCart,
  Target,
} from 'lucide-react';

// shadcn/ui Components
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

// Services
import {
  getAllProcesses,
  createProcess,
  updateProcess,
  deleteProcess,
  type Process,
} from '@/services/processService';

// Types
interface PageMetrics {
  totalProcesses: number;
  activeProcesses: number;
  criticalProcesses: number;
  underReviewProcesses: number;
}

export function ProcessesPage() {
  const { sectionColor } = useNavigation();

  // Data state
  const [processes, setProcesses] = useState<Process[]>([]);
  const [filteredProcesses, setFilteredProcesses] = useState<Process[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [businessAreaFilter, setBusinessAreaFilter] = useState<string>('all');
  const [criticalityFilter, setCriticalityFilter] = useState<string>('all');

  // Selection state
  const [selectedProcess, setSelectedProcess] = useState<Process | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  // Dialog state
  const [isAddEditOpen, setIsAddEditOpen] = useState(false);
  const [editingProcess, setEditingProcess] = useState<Process | null>(null);
  const [formData, setFormData] = useState<Partial<Process>>({});

  // Load processes
  const loadProcesses = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllProcesses();
      console.log('API response:', response);

      if (response.success && response.data) {
        // Handle both response structures (defensive)
        const processesArray = Array.isArray(response.data)
          ? response.data
          : response.data.processes;

        if (processesArray && Array.isArray(processesArray)) {
          setProcesses(processesArray);
          setFilteredProcesses(processesArray);
          console.log('Processes loaded successfully:', processesArray.length, 'processes');
        } else {
          console.warn('No valid processes array found', response);
          setProcesses([]);
          setFilteredProcesses([]);
        }
      } else {
        setError(response.error || 'Failed to load processes');
        setProcesses([]);
        setFilteredProcesses([]);
      }
    } catch (error) {
      console.error('Error loading processes:', error);
      setError(error instanceof Error ? error.message : 'Failed to load processes');
      setProcesses([]);
      setFilteredProcesses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProcesses();
  }, []);

  // Apply filters
  useEffect(() => {
    applyFilters();
  }, [processes, searchTerm, statusFilter, businessAreaFilter, criticalityFilter]);

  const applyFilters = () => {
    // Defensive check: ensure processes array exists
    if (!processes || !Array.isArray(processes)) {
      console.warn('applyFilters: processes array is undefined or not an array');
      setFilteredProcesses([]);
      return;
    }

    let filtered = [...processes];

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (process) =>
          process.name?.toLowerCase().includes(search) ||
          process.description?.toLowerCase().includes(search) ||
          process.processOwner?.toLowerCase().includes(search)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((process) => process.status === statusFilter);
    }

    // Business Area filter
    if (businessAreaFilter !== 'all') {
      filtered = filtered.filter((process) => process.businessArea === businessAreaFilter);
    }

    // Criticality filter
    if (criticalityFilter !== 'all') {
      filtered = filtered.filter((process) => process.criticality === criticalityFilter);
    }

    setFilteredProcesses(filtered);
  };

  // Calculate metrics
  const calculateMetrics = (): PageMetrics => {
    // Defensive check: ensure processes array exists
    if (!processes || !Array.isArray(processes)) {
      console.warn('calculateMetrics: processes array is undefined or not an array');
      return {
        totalProcesses: 0,
        activeProcesses: 0,
        criticalProcesses: 0,
        underReviewProcesses: 0,
      };
    }

    const activeProcesses = processes.filter((p) => p.status === 'active');
    const criticalProcesses = processes.filter((p) => p.criticality === 'critical');
    const underReviewProcesses = processes.filter((p) => p.status === 'under-review');

    return {
      totalProcesses: processes.length,
      activeProcesses: activeProcesses.length,
      criticalProcesses: criticalProcesses.length,
      underReviewProcesses: underReviewProcesses.length,
    };
  };

  const metrics = useMemo(() => calculateMetrics(), [processes]);

  // Handlers
  const handleViewDetails = (process: Process) => {
    setSelectedProcess(process);
    setIsDetailsOpen(true);
  };

  const handleAddProcess = () => {
    setEditingProcess(null);
    setFormData({});
    setIsAddEditOpen(true);
  };

  const handleEditProcess = (process: Process) => {
    setIsDetailsOpen(false);
    setEditingProcess(process);
    setFormData(process);
    setIsAddEditOpen(true);
  };

  const handleSaveProcess = async () => {
    try {
      if (editingProcess) {
        const response = await updateProcess(editingProcess.id, {
          ...formData,
          updatedAt: new Date().toISOString(),
        });
        if (response.success) {
          await loadProcesses();
          setIsAddEditOpen(false);
        } else {
          alert(response.error || 'Failed to update process');
        }
      } else {
        const response = await createProcess({
          name: formData.name || 'Untitled Process',
          ...formData,
        } as Omit<Process, 'id' | 'createdAt' | 'updatedAt'>);
        if (response.success) {
          await loadProcesses();
          setIsAddEditOpen(false);
        } else {
          alert(response.error || 'Failed to create process');
        }
      }
    } catch (error) {
      console.error('Error saving process:', error);
      alert('Failed to save process');
    }
  };

  const handleDeleteProcess = async (process: Process) => {
    if (!confirm(`Are you sure you want to delete "${process.name}"?`)) {
      return;
    }

    try {
      const response = await deleteProcess(process.id);
      if (response.success) {
        await loadProcesses();
        setIsDetailsOpen(false);
      } else {
        alert(response.error || 'Failed to delete process');
      }
    } catch (error) {
      console.error('Error deleting process:', error);
      alert('Failed to delete process');
    }
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setBusinessAreaFilter('all');
    setCriticalityFilter('all');
  };

  // Utility functions
  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'inactive':
        return 'bg-neutral-500/20 text-neutral-400 border-neutral-500/30';
      case 'under-review':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      default:
        return 'bg-neutral-500/20 text-neutral-400 border-neutral-500/30';
    }
  };

  const getCriticalityBadge = (criticality?: string) => {
    switch (criticality) {
      case 'critical':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'high':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'medium':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'low':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      default:
        return 'bg-neutral-500/20 text-neutral-400 border-neutral-500/30';
    }
  };

  // Get unique business areas for filter
  const businessAreas = useMemo(() => {
    const areas = new Set<string>();
    processes.forEach((p) => {
      if (p.businessArea) areas.add(p.businessArea);
    });
    return Array.from(areas).sort();
  }, [processes]);

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className={cn('text-3xl font-bold', sectionColor)}>Business Processes</h1>
          <p className="text-neutral-400 mt-1">Process mapping and dependencies</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className={cn('text-3xl font-bold', sectionColor)}>Business Processes</h1>
          <p className="text-neutral-400 mt-1">Process mapping and dependencies</p>
        </div>
        <Card className="p-6 border-red-500/50">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
            <div>
              <p className="text-red-500 font-medium">Failed to load processes</p>
              <p className="text-neutral-400 text-sm mt-1">{error}</p>
              <Button onClick={loadProcesses} className="mt-4 btn-primary">
                Retry
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Empty state
  if (!loading && processes.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className={cn('text-3xl font-bold', sectionColor)}>Business Processes</h1>
            <p className="text-neutral-400 mt-1">Process mapping and dependencies</p>
          </div>
          <Button onClick={handleAddProcess} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Add Process
          </Button>
        </div>

        <Card className="p-12 text-center">
          <GitBranch className="w-16 h-16 text-neutral-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No Processes Added Yet</h3>
          <p className="text-neutral-400 mb-6">
            Start mapping your business processes and their dependencies.
          </p>
          <Button onClick={handleAddProcess} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Add Your First Process
          </Button>
        </Card>
      </div>
    );
  }

  // Main render
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn('text-3xl font-bold', sectionColor)}>Business Processes</h1>
          <p className="text-neutral-400 mt-1">Process mapping and dependencies</p>
        </div>
        <Button onClick={handleAddProcess} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          Add Process
        </Button>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Total Processes</p>
              <p className="text-2xl font-bold text-white mt-1">{metrics.totalProcesses}</p>
            </div>
            <div className="p-3 bg-primary-500/20 rounded-lg">
              <GitBranch className="text-primary-400" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Active</p>
              <p className="text-2xl font-bold text-green-500 mt-1">{metrics.activeProcesses}</p>
            </div>
            <div className="p-3 bg-green-500/20 rounded-lg">
              <CheckCircle className="text-green-400" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Critical</p>
              <p className="text-2xl font-bold text-red-500 mt-1">{metrics.criticalProcesses}</p>
            </div>
            <div className="p-3 bg-red-500/20 rounded-lg">
              <AlertTriangle className="text-red-400" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Under Review</p>
              <p className="text-2xl font-bold text-yellow-500 mt-1">
                {metrics.underReviewProcesses}
              </p>
            </div>
            <div className="p-3 bg-yellow-500/20 rounded-lg">
              <Target className="text-yellow-400" size={24} />
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search Input */}
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Search processes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="under-review">Under Review</SelectItem>
            </SelectContent>
          </Select>

          {/* Criticality Filter */}
          <Select value={criticalityFilter} onValueChange={setCriticalityFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Criticality" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Criticality</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Business Area Filter (if areas exist) */}
        {businessAreas.length > 0 && (
          <div className="mt-4">
            <Select value={businessAreaFilter} onValueChange={setBusinessAreaFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Business Area" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Business Areas</SelectItem>
                {businessAreas.map((area) => (
                  <SelectItem key={area} value={area}>
                    {area}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Filter Summary & Clear */}
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-neutral-400">
            Showing {filteredProcesses?.length || 0} of {processes?.length || 0} processes
          </p>
          {(searchTerm ||
            statusFilter !== 'all' ||
            businessAreaFilter !== 'all' ||
            criticalityFilter !== 'all') && (
            <Button onClick={handleResetFilters} variant="ghost" size="sm">
              <X className="w-4 h-4 mr-2" />
              Clear Filters
            </Button>
          )}
        </div>
      </Card>

      {/* Processes Table */}
      <Card>
        <div className="card-header">
          <h2 className="text-lg font-semibold">Process Registry</h2>
        </div>
        <div className="overflow-x-auto p-6">
          {filteredProcesses.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
              <p className="text-neutral-400">No processes match your filters</p>
              <Button onClick={handleResetFilters} variant="ghost" className="mt-4">
                Clear Filters
              </Button>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-neutral-800">
                  <th className="text-left p-4 text-sm font-medium text-neutral-400">Process</th>
                  <th className="text-left p-4 text-sm font-medium text-neutral-400">
                    Business Area
                  </th>
                  <th className="text-left p-4 text-sm font-medium text-neutral-400">Owner</th>
                  <th className="text-left p-4 text-sm font-medium text-neutral-400">
                    Criticality
                  </th>
                  <th className="text-left p-4 text-sm font-medium text-neutral-400">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-neutral-400">
                    Connections
                  </th>
                  <th className="text-right p-4 text-sm font-medium text-neutral-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProcesses.map((process) => (
                  <tr
                    key={process.id}
                    className="border-b border-neutral-800/50 hover:bg-neutral-900/50"
                  >
                    <td className="p-4">
                      <p className="text-sm font-medium text-white">{process.name}</p>
                      {process.description && (
                        <p className="text-xs text-neutral-400 mt-1 line-clamp-2">
                          {process.description}
                        </p>
                      )}
                    </td>
                    <td className="p-4">
                      <p className="text-sm text-white">{process.businessArea || '-'}</p>
                    </td>
                    <td className="p-4">
                      <p className="text-sm text-white">{process.processOwner || '-'}</p>
                    </td>
                    <td className="p-4">
                      <Badge className={cn('capitalize', getCriticalityBadge(process.criticality))}>
                        {process.criticality || 'N/A'}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <Badge className={cn('capitalize', getStatusBadge(process.status))}>
                        {process.status || 'N/A'}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-wrap gap-1">
                        {process.systems && process.systems.length > 0 && (
                          <Badge variant="outline" className="text-xs">
                            <Server className="w-3 h-3 mr-1" />
                            {process.systems.length}
                          </Badge>
                        )}
                        {process.criticalPeople && process.criticalPeople.length > 0 && (
                          <Badge variant="outline" className="text-xs">
                            <Users className="w-3 h-3 mr-1" />
                            {process.criticalPeople.length}
                          </Badge>
                        )}
                        {process.suppliers && process.suppliers.length > 0 && (
                          <Badge variant="outline" className="text-xs">
                            <ShoppingCart className="w-3 h-3 mr-1" />
                            {process.suppliers.length}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          onClick={() => handleViewDetails(process)}
                          variant="ghost"
                          size="sm"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleEditProcess(process)}
                          variant="ghost"
                          size="sm"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {/* View Details Sheet */}
      <Sheet open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{selectedProcess?.name}</SheetTitle>
            <SheetDescription>{selectedProcess?.description || 'No description'}</SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            <div className="flex flex-wrap gap-2">
              <Badge className={cn('capitalize', getStatusBadge(selectedProcess?.status))}>
                {selectedProcess?.status || 'N/A'}
              </Badge>
              <Badge
                className={cn('capitalize', getCriticalityBadge(selectedProcess?.criticality))}
              >
                {selectedProcess?.criticality || 'N/A'} Criticality
              </Badge>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-white mb-3">Basic Information</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-neutral-400">Process ID:</span>
                  <span className="text-white font-mono">{selectedProcess?.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-400">Business Area:</span>
                  <span className="text-white">{selectedProcess?.businessArea || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-400">Process Owner:</span>
                  <span className="text-white">{selectedProcess?.processOwner || 'N/A'}</span>
                </div>
              </div>
            </div>

            {selectedProcess?.systems && selectedProcess.systems.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Server className="w-4 h-4" />
                  Connected Systems
                </h4>
                <div className="flex flex-wrap gap-2">
                  {selectedProcess.systems.map((system, idx) => (
                    <Badge key={idx} variant="outline">
                      {system}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {selectedProcess?.criticalPeople && selectedProcess.criticalPeople.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Critical People
                </h4>
                <div className="flex flex-wrap gap-2">
                  {selectedProcess.criticalPeople.map((person, idx) => (
                    <Badge key={idx} variant="outline">
                      {person}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {selectedProcess?.criticalSkills && selectedProcess.criticalSkills.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Required Skills
                </h4>
                <div className="flex flex-wrap gap-2">
                  {selectedProcess.criticalSkills.map((skill, idx) => (
                    <Badge key={idx} variant="outline">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {selectedProcess?.suppliers && selectedProcess.suppliers.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4" />
                  Connected Suppliers
                </h4>
                <div className="flex flex-wrap gap-2">
                  {selectedProcess.suppliers.map((supplier, idx) => (
                    <Badge key={idx} variant="outline">
                      {supplier}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {selectedProcess?.customers && selectedProcess.customers.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Customers Served
                </h4>
                <div className="flex flex-wrap gap-2">
                  {selectedProcess.customers.map((customer, idx) => (
                    <Badge key={idx} variant="outline">
                      {customer}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h4 className="text-sm font-semibold text-white mb-3">Timestamps</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-neutral-400">Created:</span>
                  <span className="text-white">
                    {selectedProcess?.createdAt
                      ? new Date(selectedProcess.createdAt).toLocaleDateString()
                      : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-400">Last Updated:</span>
                  <span className="text-white">
                    {selectedProcess?.updatedAt
                      ? new Date(selectedProcess.updatedAt).toLocaleDateString()
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-4 border-t border-neutral-800">
              <Button
                onClick={() => selectedProcess && handleEditProcess(selectedProcess)}
                className="flex-1 btn-primary"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Process
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => selectedProcess && handleDeleteProcess(selectedProcess)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Add/Edit Dialog */}
      <Dialog open={isAddEditOpen} onOpenChange={setIsAddEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProcess ? 'Edit Process' : 'Add New Process'}</DialogTitle>
            <DialogDescription>
              {editingProcess
                ? 'Update process information and save changes'
                : 'Create a new business process with all required details'}
            </DialogDescription>
          </DialogHeader>

          <div className="py-6 space-y-4">
            <div>
              <label className="form-label">Process Name *</label>
              <Input
                type="text"
                placeholder="Enter process name..."
                className="form-input"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div>
              <label className="form-label">Description</label>
              <textarea
                placeholder="Enter description..."
                className="form-input min-h-[100px]"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Business Area</label>
                <Input
                  type="text"
                  placeholder="e.g., Finance, Operations..."
                  className="form-input"
                  value={formData.businessArea || ''}
                  onChange={(e) => setFormData({ ...formData, businessArea: e.target.value })}
                />
              </div>
              <div>
                <label className="form-label">Process Owner</label>
                <Input
                  type="text"
                  placeholder="Owner name..."
                  className="form-input"
                  value={formData.processOwner || ''}
                  onChange={(e) => setFormData({ ...formData, processOwner: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Status</label>
                <Select
                  value={formData.status || 'active'}
                  onValueChange={(value) =>
                    setFormData({ ...formData, status: value as 'active' | 'inactive' | 'under-review' })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="under-review">Under Review</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="form-label">Criticality</label>
                <Select
                  value={formData.criticality || 'medium'}
                  onValueChange={(value) =>
                    setFormData({ ...formData, criticality: value as 'critical' | 'high' | 'medium' | 'low' })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select criticality" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="form-label">Systems</label>
              <Input
                type="text"
                placeholder="Comma-separated: SAP ERP, CRM Platform..."
                className="form-input"
                value={formData.systems?.join(', ') || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    systems: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  })
                }
              />
            </div>

            <div>
              <label className="form-label">Critical People</label>
              <Input
                type="text"
                placeholder="Comma-separated: John Doe, Jane Smith..."
                className="form-input"
                value={formData.criticalPeople?.join(', ') || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    criticalPeople: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  })
                }
              />
            </div>

            <div>
              <label className="form-label">Required Skills</label>
              <Input
                type="text"
                placeholder="Comma-separated: Financial Analysis, SQL..."
                className="form-input"
                value={formData.criticalSkills?.join(', ') || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    criticalSkills: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  })
                }
              />
            </div>

            <div>
              <label className="form-label">Suppliers</label>
              <Input
                type="text"
                placeholder="Comma-separated: SUPP-001, Acme Corp..."
                className="form-input"
                value={formData.suppliers?.join(', ') || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    suppliers: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  })
                }
              />
            </div>

            <div>
              <label className="form-label">Customers</label>
              <Input
                type="text"
                placeholder="Comma-separated: Enterprise Clients, SMB..."
                className="form-input"
                value={formData.customers?.join(', ') || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    customers: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  })
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddEditOpen(false)}>
              Cancel
            </Button>
            <Button className="btn-primary" onClick={handleSaveProcess}>
              {editingProcess ? 'Save Changes' : 'Create Process'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
